import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, categories } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'legend');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const filteredArticles = articles.filter((a) => a.categorySlug === activeSlug);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium">
                {currentCategory?.name || '分类'}
              </span>
            </nav>

            <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-700 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {currentCategory && (
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 font-bold mb-2">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-600 max-w-2xl">
                  {currentCategory.description}
                </p>
              </div>
            )}

            {filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden transition-all duration-300"
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="text-base md:text-lg font-heading text-foreground-900 font-semibold mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line text-xs"></i>
                          {article.publishDate}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line text-xs"></i>
                          {(article.readCount / 10000).toFixed(1)}万
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}